import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getDirectusClient } from "../../lib/directus";
import { List, Card, Switch, message, Space, Modal, Button } from "antd";
import { readPermissions, updatePermission, createPermission, readCollections, readFields } from "@directus/sdk";
import Layouts from "../../components/Layouts";

function Permissions() {
  const location = useLocation();
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [collections, setCollections] = useState([]);
  const [showSystemCollections, setShowSystemCollections] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [fields, setFields] = useState([]);
  const [isFieldModalVisible, setIsFieldModalVisible] = useState(false);
  const { id: roleId, role } = location.state || {};

  useEffect(() => {
    if (!roleId) {
      navigate("/roles");
      return;
    }
    fetchPermissions();
    fetchCollections();
  }, [roleId]);

  const fetchPermissions = async () => {
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readPermissions({
          filter: { role: { _eq: roleId } },
        })
      );
      console.log("Permisos obtenidos:", response); // Depuración
      setPermissions(response || []);
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setPermissions([]);
    }
  };

  const fetchCollections = async () => {
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(readCollections());
      console.log("Colecciones obtenidas:", response); // Depuración
      setCollections(response || []);
    } catch (error) {
      console.error("Error fetching collections:", error);
      setCollections([]);
    }
  };

  const fetchFields = async (collection) => {
    try {
      const directus = await getDirectusClient();
      const response = await directus.request(
        readFields({
          filter: { collection: { _eq: collection } }, // Filtrar campos por colección
        })
      );
      console.log("Campos obtenidos para la colección:", collection, response); // Depuración
      setFields(response || []);
    } catch (error) {
      console.error("Error fetching fields:", error);
      setFields([]);
    }
  };

  const handlePermissionChange = async (collection, action, value) => {
    try {
      const directus = await getDirectusClient();
      const existingPermission = permissions.find(
        (p) => p.collection === collection && p.action === action
      );

      if (existingPermission) {
        // Actualizar permiso existente
        await directus.request(
          updatePermission(existingPermission.id, {
            permissions: value ? {} : null,
          })
        );
      } else {
        // Crear nuevo permiso
        await directus.request(
          createPermission({
            role: roleId,
            collection: collection,
            action: action,
            permissions: value ? {} : null,
          })
        );
      }

      message.success("Permiso actualizado correctamente");
      fetchPermissions();
    } catch (error) {
      console.error("Error updating permission:", error);
      message.error("Error al actualizar el permiso");
    }
  };

  const handleFieldPermissionChange = async (collection, field, action, value) => {
    try {
      const directus = await getDirectusClient();
      const existingPermission = permissions.find(
        (p) => p.collection === collection && p.action === action && p.fields?.includes(field)
      );

      if (existingPermission) {
        // Actualizar permiso existente
        await directus.request(
          updatePermission(existingPermission.id, {
            fields: value ? [...existingPermission.fields, field] : existingPermission.fields.filter(f => f !== field),
          })
        );
      } else {
        // Crear nuevo permiso
        await directus.request(
          createPermission({
            role: roleId,
            collection: collection,
            action: action,
            fields: [field],
            permissions: {},
          })
        );
      }

      message.success("Permiso de campo actualizado correctamente");
      fetchPermissions();
    } catch (error) {
      console.error("Error updating field permission:", error);
      message.error("Error al actualizar el permiso de campo");
    }
  };

  const getPermissionForCollection = (collection, action) => {
    const permission = permissions.find(
      (p) => p.collection === collection && p.action === action
    );
    return !!permission?.permissions;
  };

  const getPermissionForField = (collection, field, action) => {
    const permission = permissions.find(
      (p) => p.collection === collection && p.action === action && p.fields?.includes(field)
    );
    return !!permission;
  };

  const showFieldModal = async (collection) => {
    setSelectedCollection(collection);
    await fetchFields(collection); // Obtener campos solo para la colección seleccionada
    setIsFieldModalVisible(true);
  };

  const closeFieldModal = () => {
    setIsFieldModalVisible(false);
    setSelectedCollection(null);
    setFields([]);
  };

  const filteredCollections = collections.filter(
    (collection) =>
      showSystemCollections || !collection.collection.startsWith("directus_")
  );

  if (!roleId || !role) {
    return null;
  }

  return (
    <Layouts>
      <Card
        title={`Permisos para el rol: ${role.name}`}
        style={{ width: "100%" }}
        extra={
          <Space>
            <span>Mostrar colecciones del sistema</span>
            <Switch
              checked={showSystemCollections}
              onChange={(checked) => setShowSystemCollections(checked)}
            />
          </Space>
        }
      >
        <List
          dataSource={filteredCollections}
          renderItem={(collection) => (
            <List.Item>
              <div style={{ width: "100%" }}>
                <h3>{collection.collection}</h3>
                <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
                  <div>
                    <span>Crear: </span>
                    <Switch
                      checked={getPermissionForCollection(collection.collection, "create")}
                      onChange={(checked) =>
                        handlePermissionChange(collection.collection, "create", checked)
                      }
                    />
                  </div>
                  <div>
                    <span>Leer: </span>
                    <Switch
                      checked={getPermissionForCollection(collection.collection, "read")}
                      onChange={(checked) =>
                        handlePermissionChange(collection.collection, "read", checked)
                      }
                    />
                  </div>
                  <div>
                    <span>Actualizar: </span>
                    <Switch
                      checked={getPermissionForCollection(collection.collection, "update")}
                      onChange={(checked) =>
                        handlePermissionChange(collection.collection, "update", checked)
                      }
                    />
                  </div>
                  <div>
                    <span>Eliminar: </span>
                    <Switch
                      checked={getPermissionForCollection(collection.collection, "delete")}
                      onChange={(checked) =>
                        handlePermissionChange(collection.collection, "delete", checked)
                      }
                    />
                  </div>
                  <Button type="link" onClick={() => showFieldModal(collection.collection)}>
                    Permisos por campo
                  </Button>
                </div>
              </div>
            </List.Item>
          )}
        />
      </Card>

      {/* Modal para permisos por campo */}
      <Modal
        title={`Permisos por campo - ${selectedCollection}`}
        visible={isFieldModalVisible}
        onCancel={closeFieldModal}
        footer={null}
        width={800}
      >
        <List
          dataSource={fields}
          renderItem={(field) => (
            <List.Item>
              <div style={{ width: "100%" }}>
                <h4>{field.field}</h4>
                <div style={{ display: "flex", gap: "16px" }}>
                  {["create", "read", "update", "delete"].map((action) => (
                    <div key={action}>
                      <span>{action}: </span>
                      <Switch
                        checked={getPermissionForField(selectedCollection, field.field, action)}
                        onChange={(checked) =>
                          handleFieldPermissionChange(selectedCollection, field.field, action, checked)
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            </List.Item>
          )}
        />
      </Modal>
    </Layouts>
  );
}

export default Permissions;